import './Notifications.scss'
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Pager } from '@progress/kendo-react-data-tools';
import { showMsg } from '../utils/logger';
import { dateToStr } from '../utils/convert';
import { get, put } from '../utils/requests';
import Button from '../buttons/Button';
import ButtonGroup from '../buttons/ButtonGroup';
import Dialog from '../dialogs/Dialog';
import Tooltip from '../forms/Tooltip';

const BUTTON_DATA = [{ iHod: 1, nHod: "Zobraziť všetky" }, { iHod: 2, nHod: "Zobraziť len neprečítané" }];

/**
 * Vrati ikonu na zaklade typu notifikacie
 * @param {*} notifTyp 
 * @returns 
 */
const getIcon = (notifTyp) => {
  switch (notifTyp.INotifTyp) {
    case 1: return "k-i-info";
    case 2: return "k-i-check-outline";
    case 3: return "k-i-warning";
    case 4: return "k-i-close-outline";
    case 5: return "k-i-loading";
    default: return;
  }
}

/**
 * Info o pocte neprecitanych notifikacii
 * @param {*} count 
 * @returns 
 */
const UnreadCount = ({ count }) => {
  if (count) {
    return <div className='notif-count active'>{`Počet neprečítaných notifikácií: ${count}`}</div>
  }
  return <div className='notif-count'>Nemáte žiadne neprečítané notifikácie</div>
}

/**
 * Riadok s notifikaciou v slideri navigacnej listy
 * @param {*} notification 
 * @returns 
 */
const CompactNotification = ({ notification, getUnreadNotifications }) => {
  const dispatch = useDispatch();

  const readNotification = async (notifTyp) => {
    const txt = `${notification.TxtNotif}\n\nDátum a čas: ${dateToStr(notification.DNotif)}`;
    dispatch(showMsg(txt, notification.NotifTyp.SkrNotifTyp, true, notification.NotifTyp.NNotifTyp));
    if (notifTyp.INotifTyp !== 5) {
      await put(`/api/meta/notif/${notification.INotif}/read`);
      await getUnreadNotifications();
    }
  }

  return (
    <div className={`notif-compact`} onClick={() => readNotification(notification.NotifTyp)}>
      <span className={`k-icon k-font-icon ${getIcon(notification.NotifTyp)}`}></span>
      <div className='notif-content'>
        <span className='notif-text'>{notification.TxtNotif}</span>
        <span className='notif-date'>{dateToStr(notification.DNotif)}</span>
      </div>
    </div>
  )
}

/**
 * Riadok s notifikaciou v dialogu notifikacii
 * @param {*} param0 
 */
const Notification = ({ notification, getUnreadNotifications, getNotifications }) => {
  const dispatch = useDispatch();

  const readAndOpenNotification = async () => {
    const txt = `${notification.TxtNotif}\n\nDátum a čas: ${dateToStr(notification.DNotif)}`;
    dispatch(showMsg(txt, notification.NotifTyp.SkrNotifTyp, true, notification.NotifTyp.NNotifTyp));
    if (!notification.LPrecit) {
      await put(`/api/meta/notif/${notification.INotif}/read`);
      await getUnreadNotifications();
      await getNotifications();
    }
  }

  const readNotification = async () => {
    if (!notification.LPrecit) {
      await put(`/api/meta/notif/${notification.INotif}/read`);
      await getUnreadNotifications();
      await getNotifications();
    }
  }

  return (
    <div className={`row notif-regular ${notification.LPrecit ? "" : "unread"}`}>
      <div className='col col-md-1 notif-icon' onClick={readAndOpenNotification}>
        <span className={`k-icon k-font-icon ${getIcon(notification.NotifTyp)}`}></span>
      </div>
      <div className='col col-md-2 notif-typ' onClick={readAndOpenNotification}>{notification.NotifTyp.NNotifTyp}</div>
      <div className='col col-md-6 notif-text' onClick={readAndOpenNotification}>{notification.TxtNotif}</div>
      <div className='col col-md-2 notif-date' onClick={readAndOpenNotification}>{dateToStr(notification.DNotif)}</div>
      <div className='col col-md-1 notif-read notif-icon' onClick={readNotification} title='Označiť ako prečítané'>
        {!notification.LPrecit &&
          <Tooltip parentTitle={true}>
            <span className={`k-icon k-font-icon k-i-check-circle`}></span>
          </Tooltip>
        }
      </div>
    </div>
  )
}

/**
 * Dialog so vsetkymi notifikaciami
 * @param {*} props 
 */
export const NotificationDialog = (props) => {
  const { isOpen, onClose, getUnreadNotifications, unreadCount } = props;
  const dispatch = useDispatch();
  const [skip, setSkip] = useState(0);
  const [notifs, setNotifs] = useState({ TotalRecords: 0, Items: [] });
  const [notifSettings, setNotifSettings] = useState({ iHod: 1, nHod: "Zobraziť všetky" });

  const handlePageChange = (event) => {
    const { skip } = event;
    setSkip(skip);
  };

  const handleChangeNotifSettings = (name, value) => {
    setNotifSettings(value);
    setSkip(0);
  }

  // Ziska notifikacie
  const getNotifications = async () => {
    try {
      const notifs = await get(`/api/meta/notif?unreadOnly=${notifSettings.iHod === 2 ? "true" : "false"}&page=${(skip / 10) + 1}`);
      setNotifs(notifs.Data);
    } catch (error) {
      dispatch(showMsg("Nepodarilo sa získať zoznam notifikácií", "warning"));
    }
  }

  // Oznacit vsetky ako precitane
  const markAllAsRead = async () => {
    await put(`/api/meta/notif/read`);
    await getUnreadNotifications();
    await getNotifications();
  }

  useEffect(() => {
    getNotifications();
  }, [notifSettings, skip]); // eslint-disable-line

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Notifikácie"
      width="70%"
      hideButtons
    >
      <div className='notif-dialog'>
        <div className='row notif-settings'>
          <div className='col'>
            <ButtonGroup
              data={BUTTON_DATA}
              dataItemKey='iHod'
              textField='nHod'
              value={notifSettings}
              onChange={handleChangeNotifSettings}
              name='notifSettings'
            />
          </div>
          <div className='col notif-mark-all-read'>
            <Button label={`Označiť všetky (${unreadCount}) ako prečítané`} icon={"check"} disabled={unreadCount === 0} onClick={markAllAsRead} primary={true} />
          </div>
        </div>
        <div className='row notif-regular notif-title'>
          <div className='col col-md-1'></div>
          <div className='col col-md-2'>Typ</div>
          <div className='col col-md-6'>Text</div>
          <div className='col col-md-2'>Dátum a čas</div>
          <div className='col col-md-1'></div>
        </div>
        {notifs?.TotalRecords > 0
          ?
          notifs?.Items?.map(notif => <Notification notification={notif} getUnreadNotifications={getUnreadNotifications} getNotifications={getNotifications} key={notif.INotif} />)
          :
          <div className='notif-empty'>Nemáte žiadne notifikácie</div>
        }
        <hr />
        <Pager
          skip={skip}
          take={10}
          total={notifs?.TotalRecords ?? 0}
          buttonCount={5}
          info={true}
          type='numeric'
          previousNext={true}
          responsive={true}
          onPageChange={handlePageChange}
        />
      </div>
    </Dialog>
  )
}

NotificationDialog.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  getUnreadNotifications: PropTypes.func,
  unreadCount: PropTypes.number
}

/**
 * Notifikacie - obsah v slideri
 * @param {*} props 
 */
export const Notifications = (props) => {
  const { unreadedNotifs, getUnreadNotifications, onOpen, markAllAsRead} = props;

  const unreadCount = unreadedNotifs?.TotalRecords || 0;
  return (
    <div className='notif-slide'>
      <UnreadCount count={unreadCount} />
      {unreadedNotifs?.Items.map(notif => <CompactNotification notification={notif} getUnreadNotifications={getUnreadNotifications} key={notif.INotif} />)}
      {/* <hr /> */}
      <div className='notif-slide-footer'>
        <Button label={`Označiť všetky ako prečítané`} fillMode={"flat"} disabled={unreadCount === 0} onClick={markAllAsRead} primary={true} />
        <Button label="Ďalšie notifikácie" onClick={onOpen} primary={true} />
      </div>

    </div>
  )
}

Notifications.propTypes = {
  unreadedNotifs: PropTypes.object,
  getUnreadNotifications: PropTypes.func,
  onOpen: PropTypes.func
}
